import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg, { generateOrganizationSchema, generateWebsiteSchema, generateFaqSchema } from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const featuredArticles = articles.slice(0, 6);

const faqItems = [
  {
    q: 'NTR是什么意思？',
    a: 'NTR是日语"寝取られ"（Netorare）的缩写，源自日本ACG文化，指伴侣或心仪对象被他人夺走的情节设定。',
  },
  {
    q: 'NTR的起源是什么？',
    a: 'NTR概念起源于日本1980-1990年代的同人创作文化，在PC游戏时代逐渐成型，通过互联网论坛固化并传播至全球。',
  },
  {
    q: 'NTR与BSS有什么区别？',
    a: 'NTR指已有恋爱关系被破坏；BSS指尚未建立关系时心仪对象被他人抢先。核心差异在于是否存在已确立的恋爱关系。',
  },
  {
    q: '为什么NTR题材会吸引特定受众？',
    a: '原因包括虚构作品提供的情感安全距离、强烈的代入感、负面情绪的审美化处理（情感净化），以及"可控的失控"心理体验。',
  },
  {
    q: 'NTR文化主要在哪些媒介中流行？',
    a: 'NTR主要在游戏（视觉小说/GalGame）、漫画（同人与商业）、动画（OVA与商业动画）三大媒介中流行。',
  },
];

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="FAQPage" />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20artistic%20background%20with%20soft%20gradient%20layers%20in%20amber%20and%20terracotta%20tones%2C%20Japanese%20minimalist%20aesthetic%2C%20subtle%20texture%20overlay%2C%20elegant%20atmospheric%20composition%2C%20clean%20and%20sophisticated%20design&width=1600&height=900&seq=ntr-hero-bg-01&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-16">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              NTR 深度解析与完整指南
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-6 max-w-2xl mx-auto leading-relaxed">
              全面介绍NTR的定义、起源、文化背景、常见场景及相关讨论，帮助读者快速、系统地了解NTR的核心概念与延伸知识，是探索NTR话题的权威参考页面。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/ding-yi"
                className="px-6 py-3 bg-primary-500 text-white rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors inline-flex items-center gap-2"
              >
                <i className="ri-book-read-line"></i>
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-white/15 text-white rounded-lg font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-white/25 transition-colors backdrop-blur-sm inline-flex items-center gap-2"
              >
                <i className="ri-information-line"></i>
                关于我们
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索 NTR 知识体系
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                通过五大分类，系统性地了解NTR文化的方方面面
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-5 text-center hover:border-primary-200 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  精选文章
                </h2>
                <p className="text-foreground-600 text-sm">
                  本站最受欢迎的内容精选
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/ding-yi"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
              >
                浏览全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm">
                关于NTR，你可能想了解这些问题
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, i) => (
                <details
                  key={i}
                  className="bg-background-50 rounded-lg border border-background-200/70 group cursor-pointer"
                >
                  <summary className="px-5 py-4 font-medium text-foreground-800 text-sm flex items-center justify-between list-none">
                    {item.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              准备好深入探索NTR的世界了吗？
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-6 max-w-lg mx-auto">
              选择你感兴趣的分类，开始系统性地了解NTR文化的各个方面
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.slice(0, 3).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="px-5 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}